import React from 'react';
import { Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination, CircularProgress } from '@mui/material';
import { colors, fonts, spacing, borderRadius, shadows, tableStyles } from '../../constants';
import { REGION_OPTIONS } from '../../constants/allQueriesPage';
import AllQueryTableRow from './AllQueryTableRow';

/**
 * AllQueriesTable Component
 * Main table component with header and body
 */
const AllQueriesTable = ({
  loading,
  total,
  queries,
  selectedRegionCard,
  onView,
  page,
  rowsPerPage,
  handleChangePage,
  handleChangeRowsPerPage,
  tableContainerRef,
}) => {
  return (
    <Paper 
      ref={tableContainerRef}
      sx={{ borderRadius: borderRadius.md, boxShadow: shadows.md, overflow: 'hidden' }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: { xs: spacing.sm, sm: spacing.md },
          py: spacing.sm,
          background: '#1e293b',
          color: colors.textWhite,
          position: 'relative',
          overflow: 'hidden',
          borderRadius: `${borderRadius.md} ${borderRadius.md} 0 0`,
          zIndex: 1,
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: '#0e172a',
            filter: 'blur(50px)',
            opacity: 0.6,
            zIndex: 0,
          },
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: fonts.weightSemiBold,
            fontSize: { xs: fonts.fontSizeXs, sm: fonts.fontSizeSm },
            letterSpacing: '0.02em',
            position: 'relative',
            zIndex: 1,
          }}
        >
          All Queries {total > 0 && `(${total})`}
        </Typography>
      </Box>
      <TableContainer 
        sx={{ 
          borderTop: `2px solid ${colors.borderDefault}`,
          maxHeight: 'calc(100vh - 400px)',
          overflow: 'auto',
          position: 'relative',
        }}
      >
        <Table sx={{ minWidth: { xs: 1000, sm: '100%' } }}>
          <TableHead sx={{ position: 'sticky', top: 0, zIndex: 10 }}>
            <TableRow sx={{ backgroundColor:'#1b2538' }}>
              <TableCell sx={{ ...tableStyles.header, color: colors.textWhite, fontWeight: fonts.weightSemiBold, backgroundColor: '#1b2538' }}>Query ID</TableCell>
              <TableCell sx={{ ...tableStyles.header, color: colors.textWhite, fontWeight: fonts.weightSemiBold, backgroundColor: '#1b2538' }}>Status</TableCell>
              <TableCell sx={{ ...tableStyles.header, color: colors.textWhite, fontWeight: fonts.weightSemiBold, backgroundColor: '#1b2538' }}>Priority</TableCell>
              <TableCell sx={{ ...tableStyles.header, color: colors.textWhite, fontWeight: fonts.weightSemiBold, backgroundColor: '#1b2538' }}>Latest Response</TableCell>
              <TableCell sx={{ ...tableStyles.header, color: colors.textWhite, fontWeight: fonts.weightSemiBold, backgroundColor: '#1b2538' }}>Raised By</TableCell>
              <TableCell sx={{ ...tableStyles.header, color: colors.textWhite, fontWeight: fonts.weightSemiBold, backgroundColor: '#1b2538' }}>Assigned To</TableCell>
              <TableCell sx={{ ...tableStyles.header, color: colors.textWhite, fontWeight: fonts.weightSemiBold, backgroundColor: '#1b2538' }}>Created Date</TableCell>
              <TableCell sx={{ ...tableStyles.header, color: colors.textWhite, fontWeight: fonts.weightSemiBold, backgroundColor: '#1b2538' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} sx={{ ...tableStyles.cell, textAlign: 'center', py: spacing.xl }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : queries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} sx={{ ...tableStyles.cell, textAlign: 'center', py: spacing.xl }}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: spacing.sm,
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          color: '#1b2538',
                          fontSize: fonts.fontSizeMd,
                          fontWeight: fonts.weightMedium,
                        }}
                      >
                        {selectedRegionCard ? `No queries found for ${REGION_OPTIONS.find((r) => r.value === selectedRegionCard)?.label || 'this region'}` : 'No queries found'}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#64748b',
                          fontSize: fonts.fontSizeSm,
                        }}
                      >
                        {selectedRegionCard ? 'Try selecting a different region or adjusting your filters' : 'Try adjusting your filters'}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                queries.map((query) => (
                  <AllQueryTableRow
                    key={query._id}
                    query={query}
                    onView={onView}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={(event, newPage) => {
          handleChangePage(event, newPage);
        }}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(event) => {
          handleChangeRowsPerPage(event);
        }}
        rowsPerPageOptions={[5, 10, 25, 50]}
        sx={{
          borderTop: `1px solid ${colors.borderDefault}`,
          '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
            fontSize: fonts.fontSizeXs,
            color: colors.textSecondary,
          },
          // "Rows per page" dropdown cursor
          '& .MuiTablePagination-select, & .MuiSelect-select': {
            cursor: 'pointer',
          },
          '& .MuiTablePagination-selectIcon, & .MuiSelect-icon': {
            cursor: 'pointer',
          },
          '& .MuiIconButton-root': {
            color: colors.textSecondary,
            '&:hover': {
              backgroundColor: colors.backgroundSoft,
            },
            '&.Mui-disabled': {
              opacity: 0.5,
            },
          },
        }}
      />
    </Paper>
  );
};

export default AllQueriesTable;
